import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  HandHeart, ShieldCheck, ArrowRight, CircleNotch, Sparkle, CheckCircle,
  Bank, UsersThree, Megaphone, Handshake, Buildings, ArrowLeft, Info, ShareNetwork,
} from "@phosphor-icons/react";
import FarmersNav from "@/components/FarmersNav";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const TABS = [
  { key: "overview",  label: "Overview" },
  { key: "readiness", label: "Get Ready" },
  { key: "needs",     label: "Farm Needs" },
  { key: "exchange",  label: "Exchange" },
  { key: "buyers",    label: "Buyers" },
  { key: "circles",   label: "Local Circles" },
  { key: "spotlight", label: "Spotlight" },
  { key: "resources", label: "Programs" },
  { key: "mentors",   label: "Mentors" },
];

const PATHWAY_ICONS = { need: HandHeart, help: Handshake, circle: UsersThree, expertise: Bank, event: Megaphone };

function Disclaimer({ text }) {
  return (
    <div data-testid="capital-disclaimer" className="rounded-xl border border-[#B85F2A]/30 bg-[#B85F2A]/8 px-4 py-3 flex gap-2.5 text-sm text-[#5a3a24] leading-snug">
      <ShieldCheck size={18} weight="fill" className="shrink-0 mt-0.5 text-[#B85F2A]" />
      <span>{text}</span>
    </div>
  );
}

function Loading() {
  return <div className="py-16 grid place-items-center text-[#3A3830]"><CircleNotch size={26} className="animate-spin" /></div>;
}

// ── Overview ──────────────────────────────────────────────────────
function OverviewTab({ data, onGoto }) {
  const [pilot, setPilot] = useState(null);
  const [pform, setPform] = useState({ role: "farm", name: "", org: "", contact: "" });
  const [joining, setJoining] = useState(false);
  useEffect(() => { http.get("/capital/pilot").then((r) => setPilot(r.data)).catch(() => {}); }, []);
  if (!data) return <Loading />;

  async function joinPilot() {
    if (!pform.name.trim()) return;
    setJoining(true);
    try { await http.post("/capital/pilot/join", pform);
      toast.success("You're on the Oregon pilot list", { description: "An organizer will reach out." });
      setPform({ role: "farm", name: "", org: "", contact: "" });
      http.get("/capital/pilot").then((r) => setPilot(r.data)); }
    catch { toast.error("Sign in to join the pilot"); } finally { setJoining(false); }
  }

  async function shareInvite() {
    let msg = "Join the FarmersPulse Oregon pilot — a free readiness & connection hub for farms (not a lender).";
    let url = typeof window !== "undefined" ? `${window.location.origin}/capital?tab=overview` : "";
    try { const r = await http.get("/capital/invite"); msg = r.data.message; url = `${window.location.origin}${r.data.path}`; } catch { /* fallback copy */ }
    try {
      if (navigator.share) await navigator.share({ title: "FarmersPulse Oregon pilot", text: msg, url });
      else { await navigator.clipboard.writeText(`${msg}\n${url}`); toast.success("Invite copied", { description: "Paste it to a farm or partner." }); }
    } catch { /* cancelled */ }
  }

  return (
    <section data-testid="capital-overview" className="space-y-6">
      <Disclaimer text={data.disclaimer} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Farm needs", data.stats.needs], ["Local circles", data.stats.circles], ["Mentors", data.stats.mentors], ["Programs", data.stats.resources]].map(([k, v]) => (
          <div key={k} className="rounded-2xl bg-white border border-black/10 p-4">
            <div className="font-serif-display text-3xl text-[#151312]">{v}</div>
            <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mt-1">{k}</div>
          </div>
        ))}
      </div>
      <div>
        <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#B85F2A] mb-3">Choose your path · {data.pilot} pilot</div>
        <div className="grid md:grid-cols-2 gap-3">
          {data.pathways.map((p) => {
            const Icon = PATHWAY_ICONS[p.key] || HandHeart;
            const tab = (p.to.split("tab=")[1]) || "overview";
            return (
              <button key={p.key} onClick={() => onGoto(tab)} data-testid={`capital-pathway-${p.key}`}
                className="group text-left rounded-2xl bg-white border border-black/10 hover:border-[#B85F2A] p-5 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-11 w-11 rounded-lg bg-[#B85F2A]/10 text-[#B85F2A]"><Icon size={24} weight="duotone" /></span>
                  <div className="font-serif-display text-xl text-[#151312]">{p.title}</div>
                </div>
                <p className="text-sm text-[#3A3830] mt-3 leading-snug">{p.blurb}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#B85F2A]">
                  {p.cta} <ArrowRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {pilot && (
        <div data-testid="capital-pilot" className="rounded-2xl bg-gradient-to-br from-[#151312] to-[#23301f] text-[#F5EBDC] p-5 md:p-6">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#F9C784]">{pilot.region} pilot · {pilot.status.replace(/_/g, " ")}</div>
          <h3 className="font-serif-display text-2xl md:text-3xl mt-1">Join the Oregon pilot</h3>
          <div className="mt-2 text-sm text-[#F5EBDC]/80 flex flex-wrap gap-x-4 gap-y-1">
            <span>{pilot.counts.farm} farms</span><span>{pilot.counts.partner} partners</span><span>{pilot.counts.mentor} mentors</span>
          </div>
          <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[13px] text-[#F5EBDC]/75 list-disc pl-5">
            {pilot.goals.map((g) => <li key={g}>{g}</li>)}
          </ul>
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            <select data-testid="pilot-role" value={pform.role} onChange={(e) => setPform({ ...pform, role: e.target.value })} className="rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-[15px] text-[#F5EBDC] outline-none">
              {pilot.roles.map((r) => <option key={r.key} value={r.key} className="text-[#151312]">{r.label}</option>)}
            </select>
            <input data-testid="pilot-name" value={pform.name} onChange={(e) => setPform({ ...pform, name: e.target.value })} placeholder="Your name" className="rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-[15px] text-[#F5EBDC] placeholder-[#F5EBDC]/50 outline-none" />
            <input data-testid="pilot-org" value={pform.org} onChange={(e) => setPform({ ...pform, org: e.target.value })} placeholder="Farm / organization (optional)" className="rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-[15px] text-[#F5EBDC] placeholder-[#F5EBDC]/50 outline-none" />
            <input data-testid="pilot-contact" value={pform.contact} onChange={(e) => setPform({ ...pform, contact: e.target.value })} placeholder="Email or phone" className="rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-[15px] text-[#F5EBDC] placeholder-[#F5EBDC]/50 outline-none" />
          </div>
          <button onClick={joinPilot} disabled={joining} data-testid="pilot-join" className="mt-3 rounded-full bg-[#F9C784] text-[#151312] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:opacity-90">{joining ? "Joining…" : "Join the pilot"}</button>
          <button onClick={shareInvite} data-testid="pilot-invite" className="mt-3 ml-2 rounded-full border border-white/30 text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:bg-white/10 inline-flex items-center gap-2"><ShareNetwork size={13} weight="bold" /> Share invite</button>
        </div>
      )}
    </section>
  );
}

// ── Get Ready (readiness workspace) ───────────────────────────────
function ReadinessTab({ requireAuth }) {
  const [d, setD] = useState(null);
  const [a, setA] = useState({});
  const [checklist, setChecklist] = useState({});
  const [draft, setDraft] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    http.get("/capital/readiness")
      .then((r) => { setD(r.data); setA(r.data.assessment || {}); setChecklist(r.data.checklist || {}); })
      .catch(() => setD({ needsAuth: true, checklist_items: [], categories: [], support_types: [] }));
  }, []);

  if (!d) return <Loading />;
  if (d.needsAuth) return (
    <div className="space-y-4">
      <Disclaimer text="This workspace is private to you. Nothing is shared until you publish a need, and only the fields you approve are shown. FarmersPulse is not a lender, broker or adviser and moves no money." />
      <div className="rounded-2xl bg-white border border-black/10 p-8 text-center" data-testid="capital-readiness-auth">
        <p className="text-[#3A3830]">Sign in to build your private readiness workspace.</p>
        <button onClick={requireAuth} data-testid="capital-readiness-signin" className="mt-4 rounded-full bg-[#151312] text-[#F5EBDC] px-6 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase">Sign in</button>
      </div>
    </div>
  );

  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));
  const done = d.checklist_items.filter((c) => checklist[c.key]).length;

  async function save() {
    setSaving(true);
    try { await http.put("/capital/readiness", { assessment: a, checklist }); toast.success("Saved — pick up anytime"); }
    catch { toast.error("Sign in to save your work"); } finally { setSaving(false); }
  }
  async function generate() {
    setDrafting(true);
    try { const r = await http.post("/capital/readiness/narrative", { assessment: a }); setDraft(r.data.draft); }
    catch { toast.error("Sign in to draft your request"); } finally { setDrafting(false); }
  }
  async function publish() {
    setPublishing(true);
    try {
      const r = await http.post("/capital/needs", {
        farm_name: a.farm_name, location: a.location, category: a.category, amount: a.amount,
        timing: a.timing, use: a.use, benefit: a.benefit, products: a.products,
        support_types: a.support_types || [], narrative: draft,
      });
      await http.post(`/capital/needs/${r.data.need.id}/submit`);
      toast.success("Submitted for mentor review", { description: "It becomes public only after a mentor approves it." });
    } catch { toast.error("Sign in to publish a need"); } finally { setPublishing(false); }
  }

  const field = (label, key, placeholder, textarea) => (
    <label className="block">
      <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">{label}</span>
      {textarea ? (
        <textarea data-testid={`readiness-${key}`} value={a[key] || ""} onChange={(e) => set(key, e.target.value)} placeholder={placeholder} rows={3}
          className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
      ) : (
        <input data-testid={`readiness-${key}`} value={a[key] || ""} onChange={(e) => set(key, e.target.value)} placeholder={placeholder}
          className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
      )}
    </label>
  );

  return (
    <section data-testid="capital-readiness" className="space-y-5">
      <Disclaimer text="This workspace is private to you. Nothing is shared until you publish a need, and only the fields you approve are shown." />
      <div className="rounded-2xl bg-white border border-black/10 p-5 md:p-6 space-y-4">
        <div className="font-serif-display text-2xl text-[#151312]">Funding-readiness</div>
        <div className="grid md:grid-cols-2 gap-4">
          {field("Farm name", "farm_name", "e.g. Cedar Hollow Farm")}
          {field("Location", "location", "County, State")}
          {field("What you grow / raise", "products", "e.g. salad greens, eggs")}
          <label className="block">
            <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">Need category</span>
            <select data-testid="readiness-category" value={a.category || ""} onChange={(e) => set("category", e.target.value)}
              className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none">
              <option value="">Select…</option>
              {d.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          {field("Amount or resource needed", "amount", "$ estimate or 'equipment share'")}
          {field("Timing", "timing", "e.g. before fall harvest")}
        </div>
        {field("Intended use — what exactly will this do?", "use", "Describe the specific use…", true)}
        {field("Community benefit — why does it matter locally?", "benefit", "Local food, jobs, soil, food access…", true)}
        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={save} disabled={saving} data-testid="readiness-save" className="rounded-full border border-black/20 px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:bg-black/[0.04]">
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button onClick={generate} disabled={drafting} data-testid="readiness-generate" className="rounded-full bg-[#B85F2A] text-white px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:opacity-90 inline-flex items-center gap-2">
            <Sparkle size={13} weight="fill" /> {drafting ? "Drafting…" : "AI: draft my request"}
          </button>
        </div>
      </div>

      {draft && (
        <div className="rounded-2xl bg-white border border-black/10 p-5 space-y-3" data-testid="readiness-draft-box">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#B85F2A]">Editable draft — your facts only</div>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={7} data-testid="readiness-draft"
            className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-[15px] leading-relaxed focus:border-[#B85F2A] outline-none" />
          <button onClick={publish} disabled={publishing} data-testid="readiness-publish" className="rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:opacity-90 inline-flex items-center gap-2">
            {publishing ? "Submitting…" : "Submit for mentor review"} <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-black/10 p-5">
        <div className="flex items-baseline justify-between">
          <div className="font-serif-display text-xl text-[#151312]">Readiness checklist</div>
          <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">{done}/{d.checklist_items.length}</div>
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          {d.checklist_items.map((c) => {
            const on = !!checklist[c.key];
            return (
              <button key={c.key} onClick={() => setChecklist((p) => ({ ...p, [c.key]: !on }))} data-testid={`readiness-check-${c.key}`}
                className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors ${on ? "border-[#2D8659] bg-[#2D8659]/8" : "border-black/12 hover:bg-black/[0.03]"}`}>
                <span className={`mt-0.5 shrink-0 grid place-items-center h-4 w-4 rounded border ${on ? "bg-[#2D8659] border-[#2D8659] text-white" : "border-black/30 text-transparent"}`}><CheckCircle size={11} weight="bold" /></span>
                <span className="text-sm text-[#151312]">{c.label}</span>
              </button>
            );
          })}
        </div>
        <button onClick={save} disabled={saving} data-testid="readiness-save-checklist" className="mt-3 rounded-full border border-black/20 px-5 py-2 font-mono-tech text-[10px] tracking-[0.26em] uppercase hover:bg-black/[0.04]">Save checklist</button>
      </div>
    </section>
  );
}

// ── Farm Needs ────────────────────────────────────────────────────
const STAGE_LABEL = { draft: "Draft", mentor_review: "Awaiting review", mentor_reviewed: "Public · mentor-reviewed", public: "Public", needs_changes: "Changes requested" };
const STAGE_CLS = { needs_changes: "bg-[#B85F2A]/12 text-[#B85F2A]", mentor_review: "bg-[#F9C784]/40 text-[#5a3a24]", mentor_reviewed: "bg-[#2D8659]/12 text-[#2D8659]", public: "bg-[#2D8659]/12 text-[#2D8659]", draft: "bg-black/8 text-[#3A3830]" };

function MyNeedRow({ n, onResubmit }) {
  const [matches, setMatches] = useState([]);
  useEffect(() => {
    http.get(`/capital/needs/${n.id}/matches`).then((r) => setMatches(r.data.matches || [])).catch(() => setMatches([]));
  }, [n.id]);
  return (
    <div data-testid={`my-need-${n.id}`} className="rounded-xl border border-black/10 px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-serif-display text-lg text-[#151312]">{n.farm_name || "Untitled request"}</span>
        <span className={`font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full ${STAGE_CLS[n.stage] || "bg-black/8 text-[#3A3830]"}`}>{STAGE_LABEL[n.stage] || n.stage}</span>
        {n.farm_verified && <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-[#2D8659]/12 text-[#2D8659] inline-flex items-center gap-1"><ShieldCheck size={10} weight="fill" /> Verified farm</span>}
      </div>
      {n.stage === "needs_changes" && n.reviewer_note && (
        <div className="mt-2 rounded-lg bg-[#B85F2A]/8 border border-[#B85F2A]/25 px-3 py-2 text-sm text-[#5a3a24]" data-testid={`my-need-note-${n.id}`}>
          <strong>Reviewer feedback:</strong> {n.reviewer_note}
          <button onClick={() => onResubmit(n.id)} data-testid={`my-need-resubmit-${n.id}`} className="ml-2 underline font-semibold">Resubmit</button>
        </div>
      )}
      {!!matches.length && (
        <div className="mt-2 rounded-lg bg-[#2D8659]/8 border border-[#2D8659]/25 px-3 py-2" data-testid={`my-need-matches-${n.id}`}>
          <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#2D8659] mb-1">Buyers who might want this</div>
          {matches.slice(0, 3).map((m, i) => (
            <div key={i} className="text-[13px] text-[#3A3830]">• <strong className="text-[#151312]">{m.buyer.name}</strong> — {m.buyer.product}{m.same_area ? " · same area" : ""}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function NeedsTab({ requireAuth }) {
  const [data, setData] = useState(null);
  const [mine, setMine] = useState([]);
  const [filter, setFilter] = useState("");
  const loadMine = () => http.get("/capital/needs/mine").then((r) => setMine(r.data.needs || [])).catch(() => setMine([]));
  useEffect(() => { http.get("/capital/needs").then((r) => setData(r.data)).catch(() => setData({ needs: [], support_types: [] })); loadMine(); }, []);
  if (!data) return <Loading />;
  const cats = Array.from(new Set(data.needs.map((n) => n.category).filter(Boolean)));
  const shown = filter ? data.needs.filter((n) => n.category === filter) : data.needs;

  async function support(need) {
    try { await http.post(`/capital/needs/${need.id}/support`, { kind: "advice", message: "I'd like to help." });
      toast.success("Thanks — your offer was recorded", { description: "FarmersPulse does not move money or guarantee outcomes." }); }
    catch { requireAuth(); }
  }
  async function resubmit(id) {
    try { await http.post(`/capital/needs/${id}/submit`); toast.success("Resubmitted for review"); loadMine(); }
    catch { requireAuth(); }
  }

  return (
    <section data-testid="capital-needs" className="space-y-4">
      <Disclaimer text="Every public need belongs to a verified farm and shows its review status. Support is non-financial: advice, buyer interest, equipment or program referrals." />

      {!!mine.length && (
        <div className="rounded-2xl bg-white border border-black/10 p-5" data-testid="capital-my-needs">
          <div className="font-serif-display text-xl text-[#151312] mb-2">Your requests</div>
          <div className="space-y-2">
            {mine.map((n) => <MyNeedRow key={n.id} n={n} onResubmit={resubmit} />)}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase border ${!filter ? "bg-[#151312] text-[#F5EBDC] border-[#151312]" : "border-black/15 bg-white"}`} data-testid="needs-filter-all">All</button>
        {cats.map((c) => (
          <button key={c} onClick={() => setFilter(c)} className={`rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase border ${filter === c ? "bg-[#151312] text-[#F5EBDC] border-[#151312]" : "border-black/15 bg-white"}`}>{c}</button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {shown.map((n) => (
          <div key={n.id} data-testid={`need-card-${n.id}`} className="rounded-2xl bg-white border border-black/10 p-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-[#B85F2A]/10 text-[#B85F2A]">{n.category}</span>
              {n.is_example && <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-black/8 text-[#3A3830]">Example</span>}
              {n.farm_verified && <span data-testid={`need-verified-${n.id}`} className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-[#2D8659]/12 text-[#2D8659] inline-flex items-center gap-1"><ShieldCheck size={10} weight="fill" /> Verified farm</span>}
              {n.stage === "mentor_reviewed" && <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-[#2D8659]/12 text-[#2D8659]">Mentor-reviewed</span>}
            </div>
            <div className="font-serif-display text-xl text-[#151312] mt-2">{n.farm_name}</div>
            <div className="text-xs text-[#3A3830] mt-0.5">{n.location} · {n.amount} · {n.timing}</div>
            <p className="text-sm text-[#3A3830] mt-2 leading-snug">{n.use}</p>
            <p className="text-[13px] text-[#215c40] mt-2 leading-snug"><strong>Benefit:</strong> {n.benefit}</p>
            <button onClick={() => support(n)} data-testid={`need-support-${n.id}`} className="mt-3 rounded-full border border-black/20 px-4 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/[0.04] inline-flex items-center gap-1.5">
              <Handshake size={13} weight="bold" /> Offer help
            </button>
          </div>
        ))}
        {!shown.length && <div className="text-[#3A3830] text-sm">No needs in this category yet.</div>}
      </div>
    </section>
  );
}

// ── Circles ───────────────────────────────────────────────────────
function CirclesTab({ requireAuth }) {
  const [circles, setCircles] = useState(null);
  const [form, setForm] = useState({ name: "", geography: "", mission: "" });
  const [creating, setCreating] = useState(false);
  const load = () => http.get("/capital/circles").then((r) => setCircles(r.data.circles)).catch(() => setCircles([]));
  useEffect(() => { load(); }, []);
  if (!circles) return <Loading />;

  async function create() {
    if (!form.name.trim()) return;
    setCreating(true);
    try { await http.post("/capital/circles", form); toast.success("Circle created"); setForm({ name: "", geography: "", mission: "" }); load(); }
    catch { requireAuth(); } finally { setCreating(false); }
  }
  async function join(id) {
    try { await http.post(`/capital/circles/${id}/join`); toast.success("Joined — an organizer will follow up"); load(); }
    catch { requireAuth(); }
  }

  return (
    <section data-testid="capital-circles" className="space-y-4">
      <Disclaimer text="Circles are for community, mentorship and referrals — not for negotiating loans, notes or investments." />
      <div className="grid md:grid-cols-2 gap-3">
        {circles.map((c) => (
          <div key={c.id} data-testid={`circle-card-${c.id}`} className="rounded-2xl bg-white border border-black/10 p-5">
            <div className="font-serif-display text-xl text-[#151312]">{c.name}</div>
            <div className="text-xs text-[#B85F2A] font-mono-tech tracking-[0.18em] uppercase mt-1">{c.geography}</div>
            <p className="text-sm text-[#3A3830] mt-2 leading-snug">{c.mission}</p>
            <div className="text-xs text-[#3A3830] mt-2">Organizer: {c.organizer} · {c.members_count || 0} members</div>
            <button onClick={() => join(c.id)} data-testid={`circle-join-${c.id}`} className="mt-3 rounded-full border border-black/20 px-4 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/[0.04]">Join circle</button>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-white border border-black/10 p-5 space-y-3">
        <div className="font-serif-display text-xl text-[#151312]">Start a local circle</div>
        <input data-testid="circle-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Circle name (e.g. Rogue Valley Growers)" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <input data-testid="circle-geography" value={form.geography} onChange={(e) => setForm({ ...form, geography: e.target.value })} placeholder="Geography (county / foodshed / state)" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <textarea data-testid="circle-mission" value={form.mission} onChange={(e) => setForm({ ...form, mission: e.target.value })} placeholder="Mission" rows={2} className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <button onClick={create} disabled={creating} data-testid="circle-create" className="rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:opacity-90">{creating ? "Creating…" : "Create circle"}</button>
      </div>
    </section>
  );
}

// ── Spotlight ─────────────────────────────────────────────────────
function SpotlightTab({ requireAuth }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ farm_name: "", challenge: "", requested_help: "" });
  const [sched, setSched] = useState({ when: "", where: "", status: "accepting_applications" });
  const [applying, setApplying] = useState(false);
  const load = () => http.get("/capital/spotlight").then((r) => setData(r.data)).catch(() => setData({ events: [], applications: [], workflow: [] }));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  async function apply() {
    if (!form.farm_name.trim() || !form.challenge.trim()) return;
    setApplying(true);
    try { await http.post("/capital/spotlight/apply", { event_id: data.events[0]?.id || "spotlight-or-2026", ...form });
      toast.success("Application submitted", { description: "An organizer will screen it for clarity and eligibility." });
      setForm({ farm_name: "", challenge: "", requested_help: "" }); load(); }
    catch { requireAuth(); } finally { setApplying(false); }
  }
  async function schedule(id) {
    try { await http.post(`/capital/spotlight/${id}/schedule`, sched); toast.success("Spotlight scheduled — now live"); load(); }
    catch { requireAuth(); }
  }
  async function rsvp(e) {
    try {
      if (e.rsvped) { await http.delete(`/capital/spotlight/${e.id}/rsvp`); toast.success("RSVP removed"); }
      else { await http.post(`/capital/spotlight/${e.id}/rsvp`); toast.success("You're going!", { description: "We'll remind you before it starts." }); }
      load();
    } catch { requireAuth(); }
  }

  return (
    <section data-testid="capital-spotlight" className="space-y-4">
      <Disclaimer text="Farms present a real challenge and get mentor feedback + introductions. No money changes hands at the event; any financing happens off-platform." />
      <div className="rounded-2xl bg-white border border-black/10 p-5">
        <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] flex flex-wrap gap-2">
          {data.workflow.map((s, i) => <span key={s} className="inline-flex items-center gap-1.5">{i > 0 && <ArrowRight size={11} />} {s}</span>)}
        </div>
      </div>
      {data.events.map((e) => (
        <div key={e.id} data-testid={`spotlight-event-${e.id}`} className="rounded-2xl bg-white border border-black/10 p-5">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-serif-display text-2xl text-[#151312]">{e.title}</div>
            {e.scheduled && <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-[#2D8659]/12 text-[#2D8659]">Scheduled</span>}
          </div>
          <div className="text-xs text-[#B85F2A] font-mono-tech tracking-[0.18em] uppercase mt-1">{e.when} · {e.where}</div>
          <p className="text-sm text-[#3A3830] mt-2 leading-snug">{e.summary}</p>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button onClick={() => rsvp(e)} data-testid={`spotlight-rsvp-${e.id}`}
              className={`rounded-full px-5 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-1.5 ${e.rsvped ? "bg-[#2D8659] text-white" : "border border-black/20 hover:bg-black/[0.04]"}`}>
              <CheckCircle size={13} weight={e.rsvped ? "fill" : "regular"} /> {e.rsvped ? "Going — you're on the list" : "RSVP · remind me"}
            </button>
            {!!e.rsvp_count && <span className="text-xs text-[#3A3830]" data-testid={`spotlight-rsvp-count-${e.id}`}>{e.rsvp_count} attending</span>}
          </div>
          {data.is_admin && (
            <div className="mt-4 rounded-xl border border-[#B85F2A]/25 bg-[#B85F2A]/6 p-4 space-y-2" data-testid={`spotlight-schedule-${e.id}`}>
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#B85F2A]">Organizer · schedule this event</div>
              <div className="grid sm:grid-cols-2 gap-2">
                <input data-testid="spotlight-when" value={sched.when} onChange={(ev) => setSched({ ...sched, when: ev.target.value })} placeholder="When (e.g. Sat Oct 18, 10am)" className="rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
                <input data-testid="spotlight-where" value={sched.where} onChange={(ev) => setSched({ ...sched, where: ev.target.value })} placeholder="Where (venue / online)" className="rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
              </div>
              <button onClick={() => schedule(e.id)} data-testid={`spotlight-save-${e.id}`} className="rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.26em] uppercase hover:opacity-90">Publish schedule</button>
            </div>
          )}
        </div>
      ))}
      <div className="rounded-2xl bg-white border border-black/10 p-5 space-y-3">
        <div className="font-serif-display text-xl text-[#151312]">Apply to present</div>
        <input data-testid="spotlight-farm" value={form.farm_name} onChange={(e) => setForm({ ...form, farm_name: e.target.value })} placeholder="Farm name" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <textarea data-testid="spotlight-challenge" value={form.challenge} onChange={(e) => setForm({ ...form, challenge: e.target.value })} placeholder="Your challenge or growth opportunity" rows={3} className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <input data-testid="spotlight-help" value={form.requested_help} onChange={(e) => setForm({ ...form, requested_help: e.target.value })} placeholder="What help are you looking for?" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <button onClick={apply} disabled={applying} data-testid="spotlight-apply" className="rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:opacity-90">{applying ? "Submitting…" : "Submit application"}</button>
        {!!data.applications.length && <div className="text-xs text-[#2D8659] font-mono-tech tracking-[0.18em] uppercase pt-1" data-testid="spotlight-mine">You have {data.applications.length} application(s) submitted.</div>}
      </div>
    </section>
  );
}

// ── Resources ─────────────────────────────────────────────────────
function ResourcesTab() {
  const [data, setData] = useState(null);
  const [type, setType] = useState("");
  const load = (t) => http.get(`/capital/resources${t ? `?type=${t}` : ""}`).then((r) => setData(r.data)).catch(() => setData({ resources: [], types: [] }));
  useEffect(() => { load(""); }, []);
  if (!data) return <Loading />;
  return (
    <section data-testid="capital-resources" className="space-y-4">
      <Disclaimer text="These link to official sources. Always confirm eligibility and deadlines on the provider's own site before applying." />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => { setType(""); load(""); }} className={`rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase border ${!type ? "bg-[#151312] text-[#F5EBDC] border-[#151312]" : "border-black/15 bg-white"}`} data-testid="resources-filter-all">All</button>
        {data.types.map((t) => (
          <button key={t} onClick={() => { setType(t); load(t); }} className={`rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase border ${type === t ? "bg-[#151312] text-[#F5EBDC] border-[#151312]" : "border-black/15 bg-white"}`} data-testid={`resources-filter-${t}`}>{t}</button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {data.resources.map((r) => (
          <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`resource-${r.id}`} className="group rounded-2xl bg-white border border-black/10 hover:border-[#B85F2A] p-5 transition-colors block">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-[#B85F2A]/10 text-[#B85F2A]">{r.type}</span>
              <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">{r.geography}</span>
            </div>
            <div className="font-serif-display text-lg text-[#151312] mt-2 flex items-center gap-1.5">{r.name} <ArrowRight size={13} weight="bold" className="opacity-0 group-hover:opacity-100 transition-opacity text-[#B85F2A]" /></div>
            <div className="text-xs text-[#3A3830] mt-1">{r.provider}</div>
            <p className="text-[13px] text-[#3A3830] mt-2 leading-snug">{r.eligibility}</p>
            <div className="text-xs text-[#3A3830] mt-2 font-mono-tech tracking-[0.14em] uppercase">Deadline: {r.deadline} · verified {r.last_verified}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

// ── Mentors ───────────────────────────────────────────────────────
function MentorsTab({ requireAuth }) {
  const [mentors, setMentors] = useState(null);
  const [form, setForm] = useState({ name: "", specialty: "", geography: "", disclosures: "" });
  const [saving, setSaving] = useState(false);
  const load = () => http.get("/capital/mentors").then((r) => setMentors(r.data.mentors)).catch(() => setMentors([]));
  useEffect(() => { load(); }, []);
  if (!mentors) return <Loading />;
  async function join() {
    if (!form.name.trim() || !form.specialty.trim()) return;
    setSaving(true);
    try { await http.post("/capital/mentors", form); toast.success("You're listed as a mentor"); setForm({ name: "", specialty: "", geography: "", disclosures: "" }); load(); }
    catch { requireAuth(); } finally { setSaving(false); }
  }
  return (
    <section data-testid="capital-mentors" className="space-y-4">
      <Disclaimer text="Mentors give guidance, not individualized financial, legal or tax advice. Please disclose any conflicts, fees or referral relationships." />
      <div className="grid md:grid-cols-2 gap-3">
        {mentors.map((m) => (
          <div key={m.id} data-testid={`mentor-card-${m.id}`} className="rounded-2xl bg-white border border-black/10 p-5">
            <div className="font-serif-display text-lg text-[#151312]">{m.name}</div>
            <div className="text-xs text-[#B85F2A] font-mono-tech tracking-[0.18em] uppercase mt-1">{m.specialty}{m.geography ? ` · ${m.geography}` : ""}</div>
            {m.credentials && <p className="text-[13px] text-[#3A3830] mt-2">{m.credentials}</p>}
            {m.disclosures && <p className="text-xs text-[#3A3830] mt-1"><Info size={11} className="inline" /> {m.disclosures}</p>}
          </div>
        ))}
        {!mentors.length && <div className="text-[#3A3830] text-sm">No mentors listed yet — be the first.</div>}
      </div>
      <div className="rounded-2xl bg-white border border-black/10 p-5 space-y-3">
        <div className="font-serif-display text-xl text-[#151312]">Offer expertise</div>
        <input data-testid="mentor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name / organization" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <input data-testid="mentor-specialty" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="Specialty (e.g. farm accounting, grants, markets)" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <input data-testid="mentor-geography" value={form.geography} onChange={(e) => setForm({ ...form, geography: e.target.value })} placeholder="Geography served" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <input data-testid="mentor-disclosures" value={form.disclosures} onChange={(e) => setForm({ ...form, disclosures: e.target.value })} placeholder="Conflict / fee / referral disclosures" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <button onClick={join} disabled={saving} data-testid="mentor-join" className="rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:opacity-90">{saving ? "Saving…" : "List me as a mentor"}</button>
      </div>
    </section>
  );
}

// ── Farm Needs Exchange (non-cash swaps) ──────────────────────────
function ExchangeTab({ requireAuth }) {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("");
  const [form, setForm] = useState({ kind: "offer", category: "", title: "", description: "", location: "", contact: "" });
  const [saving, setSaving] = useState(false);
  const load = () => http.get("/capital/exchange").then((r) => setData(r.data)).catch(() => setData({ listings: [], categories: [] }));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;
  const shown = kind ? data.listings.filter((x) => x.kind === kind) : data.listings;

  async function post() {
    if (!form.title.trim() || !form.category) return;
    setSaving(true);
    try { await http.post("/capital/exchange", form); toast.success("Listing posted", { description: "It's a non-cash listing — arrange details directly." });
      setForm({ kind: "offer", category: "", title: "", description: "", location: "", contact: "" }); load(); }
    catch { requireAuth(); } finally { setSaving(false); }
  }

  return (
    <section data-testid="capital-exchange" className="space-y-4">
      <Disclaimer text="The Exchange is for non-cash help — lending equipment, sharing storage, land, labor or transport. FarmersPulse is not a party to any exchange." />
      <div className="flex flex-wrap gap-2">
        {[["", "All"], ["offer", "Offers"], ["request", "Requests"]].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setKind(k)} data-testid={`exchange-filter-${k || "all"}`} className={`rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase border ${kind === k ? "bg-[#151312] text-[#F5EBDC] border-[#151312]" : "border-black/15 bg-white"}`}>{l}</button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {shown.map((x) => (
          <div key={x.id} data-testid={`exchange-card-${x.id}`} className="rounded-2xl bg-white border border-black/10 p-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full ${x.kind === "offer" ? "bg-[#2D8659]/12 text-[#2D8659]" : "bg-[#B85F2A]/10 text-[#B85F2A]"}`}>{x.kind}</span>
              <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">{x.category}</span>
              {x.is_example && <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-black/8 text-[#3A3830]">Example</span>}
            </div>
            <div className="font-serif-display text-lg text-[#151312] mt-2">{x.title}</div>
            <p className="text-sm text-[#3A3830] mt-1 leading-snug">{x.description}</p>
            <div className="text-xs text-[#3A3830] mt-2">{x.location}</div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-white border border-black/10 p-5 space-y-3">
        <div className="font-serif-display text-xl text-[#151312]">Post to the exchange</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <select data-testid="exchange-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none">
            <option value="offer">I'm offering</option><option value="request">I'm looking for</option>
          </select>
          <select data-testid="exchange-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none">
            <option value="">Category…</option>{data.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <input data-testid="exchange-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short title" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <textarea data-testid="exchange-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Details" rows={2} className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <input data-testid="exchange-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (county / region)" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <button onClick={post} disabled={saving} data-testid="exchange-post" className="rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:opacity-90">{saving ? "Posting…" : "Post listing"}</button>
      </div>
    </section>
  );
}

// ── Buyer-Backed Growth ───────────────────────────────────────────
function BuyersTab({ requireAuth }) {
  const [data, setData] = useState(null);
  const [matches, setMatches] = useState([]);
  const [form, setForm] = useState({ buyer_name: "", buyer_type: "Restaurant", product: "", quantity: "", timing: "", location: "", contact: "" });
  const [saving, setSaving] = useState(false);
  const load = () => { http.get("/capital/buyers").then((r) => setData(r.data)).catch(() => setData({ buyers: [], buyer_types: [] })); http.get("/capital/matches").then((r) => setMatches(r.data.matches || [])).catch(() => setMatches([])); };
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  async function post() {
    if (!form.buyer_name.trim() || !form.product.trim()) return;
    setSaving(true);
    try { await http.post("/capital/buyers", form); toast.success("Interest posted", { description: "Nonbinding — no payment is taken here." });
      setForm({ buyer_name: "", buyer_type: "Restaurant", product: "", quantity: "", timing: "", location: "", contact: "" }); load(); }
    catch { requireAuth(); } finally { setSaving(false); }
  }

  return (
    <section data-testid="capital-buyers" className="space-y-4">
      <Disclaimer text="Buyers post nonbinding preorder interest so farms can validate demand before seeking financing. No payment is taken on FarmersPulse." />

      {!!matches.length && (
        <div className="rounded-2xl bg-[#2D8659]/8 border border-[#2D8659]/25 p-5" data-testid="capital-matches">
          <div className="font-serif-display text-xl text-[#151312] flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-[#2D8659]" /> Suggested matches</div>
          <p className="text-xs text-[#3A3830] mt-1 mb-3">Possible fits by product &amp; area — a nonbinding hint, not a deal.</p>
          <div className="space-y-2">
            {matches.slice(0, 6).map((m, i) => (
              <div key={i} data-testid="match-row" className="rounded-xl bg-white border border-black/10 px-4 py-3 text-sm">
                <span className="font-semibold text-[#151312]">{m.buyer.name}</span>
                <span className="text-[#3A3830]"> wants {m.buyer.product} </span>
                <ArrowRight size={12} weight="bold" className="inline text-[#2D8659]" />
                <span className="font-semibold text-[#151312]"> {m.need.farm_name}</span>
                <span className="text-[#3A3830]"> ({m.need.category})</span>
                {!!m.shared.length && <span className="ml-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#2D8659]">shared: {m.shared.join(", ")}</span>}
                {m.same_area && <span className="ml-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#B85F2A]">same area</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {data.buyers.map((b) => (
          <div key={b.id} data-testid={`buyer-card-${b.id}`} className="rounded-2xl bg-white border border-black/10 p-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-[#B85F2A]/10 text-[#B85F2A]">{b.buyer_type}</span>
              {b.is_example && <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-black/8 text-[#3A3830]">Example</span>}
            </div>
            <div className="font-serif-display text-lg text-[#151312] mt-2">{b.buyer_name}</div>
            <p className="text-sm text-[#3A3830] mt-1 leading-snug"><strong>Wants:</strong> {b.product}</p>
            <div className="text-xs text-[#3A3830] mt-1">{b.quantity} · {b.timing} · {b.location}</div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-white border border-black/10 p-5 space-y-3">
        <div className="font-serif-display text-xl text-[#151312]">Post buyer interest</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <input data-testid="buyer-name" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} placeholder="Buyer name (restaurant, school…)" className="rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
          <select data-testid="buyer-type" value={form.buyer_type} onChange={(e) => setForm({ ...form, buyer_type: e.target.value })} className="rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none">
            {data.buyer_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <input data-testid="buyer-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="What are you looking to buy?" className="w-full rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        <div className="grid sm:grid-cols-3 gap-2">
          <input data-testid="buyer-quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="Quantity" className="rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
          <input data-testid="buyer-timing" value={form.timing} onChange={(e) => setForm({ ...form, timing: e.target.value })} placeholder="Timing / season" className="rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
          <input data-testid="buyer-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className="rounded-lg border border-black/15 px-3 py-2 text-[15px] focus:border-[#B85F2A] outline-none" />
        </div>
        <button onClick={post} disabled={saving} data-testid="buyer-post" className="rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:opacity-90">{saving ? "Posting…" : "Post interest"}</button>
      </div>
    </section>
  );
}

// ── Mentor Approval Console (admin / organizer only) ──────────────
function ReviewTab({ requireAuth }) {
  const [rows, setRows] = useState(null);
  const [signups, setSignups] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const load = () => http.get("/capital/admin/needs/pending").then((r) => setRows(r.data.needs)).catch((e) => setRows(e?.response?.status === 403 ? "forbidden" : []));
  useEffect(() => { load(); http.get("/capital/admin/pilot/signups").then((r) => setSignups(r.data.signups || [])).catch(() => setSignups([])); }, []);
  if (rows === null) return <Loading />;
  if (rows === "forbidden") return (
    <div className="rounded-2xl bg-white border border-black/10 p-8 text-center" data-testid="review-forbidden">
      <p className="text-[#3A3830]">This review console is for organizers and admins.</p>
    </div>
  );

  async function decide(id, action, note) {
    setBusyId(id);
    try { await http.post(`/capital/needs/${id}/${action}`, { note: note || "" });
      toast.success(action === "approve" ? "Published — now public" : "Sent back to the farmer"); load(); }
    catch { requireAuth(); } finally { setBusyId(null); }
  }

  return (
    <section data-testid="capital-review" className="space-y-4">
      <Disclaimer text="Only mentor-reviewed needs go public. Approve requests that are clear, honest and eligible; send others back with feedback. Approval is not an endorsement or a promise of funding." />

      {!!signups.length && (
        <div className="rounded-2xl bg-white border border-black/10 p-5" data-testid="review-cohort">
          <div className="font-serif-display text-xl text-[#151312] mb-2">Oregon pilot cohort · {signups.length}</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {signups.map((s) => (
              <div key={s.id} className="rounded-xl border border-black/10 px-3 py-2 text-sm">
                <span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full bg-[#B85F2A]/10 text-[#B85F2A] mr-1">{s.role}</span>
                <span className="font-semibold text-[#151312]">{s.name}</span>
                {s.org && <span className="text-[#3A3830]"> · {s.org}</span>}
                {s.contact && <span className="text-[#3A3830] block text-xs">{s.contact}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {!rows.length && <div className="rounded-2xl bg-white border border-black/10 p-8 text-center text-[#3A3830]" data-testid="review-empty">No needs awaiting review. 🎉</div>}
      <div className="space-y-3">
        {(rows || []).map((n) => (
          <div key={n.id} data-testid={`review-card-${n.id}`} className="rounded-2xl bg-white border border-black/10 p-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-[#B85F2A]/10 text-[#B85F2A]">{n.category}</span>
              <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-[#F9C784]/30 text-[#5a3a24]">Awaiting review</span>
            </div>
            <div className="font-serif-display text-xl text-[#151312] mt-2">{n.farm_name || "Unnamed farm"}</div>
            <div className="text-xs text-[#3A3830] mt-0.5">{n.location} · {n.amount} · {n.timing}</div>
            <p className="text-sm text-[#3A3830] mt-2 leading-snug"><strong>Use:</strong> {n.use}</p>
            <p className="text-[13px] text-[#215c40] mt-1 leading-snug"><strong>Benefit:</strong> {n.benefit}</p>
            {n.narrative && <p className="text-[13px] text-[#3A3830] mt-2 whitespace-pre-line border-l-2 border-black/10 pl-3">{n.narrative}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => decide(n.id, "approve")} disabled={busyId === n.id} data-testid={`review-approve-${n.id}`} className="rounded-full bg-[#2D8659] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:opacity-90">Approve &amp; publish</button>
              <button onClick={() => decide(n.id, "reject", "Please add more detail and resubmit.")} disabled={busyId === n.id} data-testid={`review-reject-${n.id}`} className="rounded-full border border-black/20 px-4 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/[0.04]">Send back</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function FarmersCapital() {
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "overview");
  const [overview, setOverview] = useState(null);
  const { user, openLogin } = useAuth();

  useEffect(() => { setSp((p) => { p.set("tab", tab); return p; }, { replace: true }); }, [tab, setSp]);
  useEffect(() => { http.get("/capital/overview").then((r) => setOverview(r.data)).catch(() => {}); }, []);

  const requireAuth = () => { if (!user) openLogin?.(); };
  const listenText = "FarmersPulse Community Capital and Farm Support. A readiness, education and connection hub. FarmersPulse is not a lender, broker or investment adviser and no money moves through this hub.";

  return (
    <div className="min-h-screen bg-[#F5EBDC] text-[#151312]">
      <FarmersNav />
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6 pb-24">
        <a href="/" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830] hover:text-[#151312]"><ArrowLeft size={12} /> Farmers Pulse</a>
        <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#B85F2A] mt-4">FarmersPulse · Community Capital</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl leading-[1.02] mt-1">Community Capital &amp; Farm Support</h1>
        <p className="text-base text-[#3A3830] mt-3 max-w-2xl leading-relaxed">Get funding-ready, publish a verified need, meet mentors, buyers and local supporters, and find official programs — all without FarmersPulse touching a cent.</p>
        <div className="mt-4"><ListenTranslateBar text={listenText} testidPrefix="capital-listen" /></div>

        <div className="mt-6 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`capital-tab-${t.key}`}
              className={`rounded-full border px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition ${tab === t.key ? "border-[#B85F2A] bg-[#151312] text-[#F5EBDC]" : "border-black/10 bg-white/70 text-[#3A3830] hover:border-[#B85F2A]"}`}>
              {t.label}
            </button>
          ))}
          {overview?.is_admin && (
            <button onClick={() => setTab("review")} data-testid="capital-tab-review"
              className={`rounded-full border px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition inline-flex items-center gap-1.5 ${tab === "review" ? "border-[#B85F2A] bg-[#151312] text-[#F5EBDC]" : "border-[#B85F2A]/40 bg-[#B85F2A]/8 text-[#B85F2A] hover:bg-[#B85F2A]/15"}`}>
              <ShieldCheck size={12} weight="fill" /> Review{overview?.pending_review ? ` (${overview.pending_review})` : ""}
            </button>
          )}
        </div>

        <div className="mt-6">
          {tab === "overview"  && <OverviewTab data={overview} onGoto={setTab} />}
          {tab === "readiness" && <ReadinessTab requireAuth={requireAuth} />}
          {tab === "needs"     && <NeedsTab requireAuth={requireAuth} />}
          {tab === "exchange"  && <ExchangeTab requireAuth={requireAuth} />}
          {tab === "buyers"    && <BuyersTab requireAuth={requireAuth} />}
          {tab === "circles"   && <CirclesTab requireAuth={requireAuth} />}
          {tab === "spotlight" && <SpotlightTab requireAuth={requireAuth} />}
          {tab === "resources" && <ResourcesTab />}
          {tab === "mentors"   && <MentorsTab requireAuth={requireAuth} />}
          {tab === "review"    && <ReviewTab requireAuth={requireAuth} />}
        </div>
      </div>
    </div>
  );
}
